import { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setNetworkConnect, setProgressSync, setForceSync } from 'reducers/network';
import { fetchFarmGroups } from 'reducers/dailyCheckup/checkupFarmGroups';
import { fetchFarms } from 'reducers/dailyCheckup/checkupFarms';
// offline
import { getLastPreloadData, updateLastPreloadData, clearOfflineCheckups,
  clearOfflineRequests, clearOfflineMediaRequests } from 'utils/offlineHelper';
import { clearFeedOrdersListsOffline, updateFeedOrdersPreloadDate } from 'utils/offlineFeedOrdersHelper';
// utils
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import { getLSRefreshIndexedDB, removeLSRefreshIndexedDB } from 'utils/localStorageHelper';
import { isCheckupPigGroupsLocation, isCheckupFarmsLocation } from 'utils';
// api
import { getSymptoms, getTreatments, getMortalityReasons, getTemperaturePoints, getFarmsForOffline,
  getPigGroupsForOffline } from 'endpoints/common';
import { execDailyCheckupsSync, execMediaSync } from 'endpoints/checkup/checkupEdit';
import { execFeedOrdersSync, preloadFeedOrdersData } from 'endpoints/feedOrders';

const FEED_ORDERS_SYNC_INTERVAL = 60000;

class NetworkManager extends Component {

  componentDidMount() {
    window.addEventListener('online', this.handleOnline, false);
    window.addEventListener('offline', this.handleOffline, false);
    this.feedOrdersSyncTimer = setInterval(this.retryFeedOrdersSync, FEED_ORDERS_SYNC_INTERVAL);
    this.initialPreloadData();
  }

  componentDidUpdate({ current_company_id: prevCompanyId }) {
    const { current_company_id, isForceSync } = this.props;
    if ((prevCompanyId !== current_company_id && current_company_id) || isForceSync) {
      this.sendRequestsForOffline(current_company_id);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline, false);
    window.removeEventListener('offline', this.handleOffline, false);
    clearInterval(this.feedOrdersSyncTimer);
  }

  isSyncStarted = false;

  syncEntities = new Set();

  startSync = (requestsCount, syncingData = 'syncingDC') => {
    const isRequestsExists = requestsCount > 0;
    if (isRequestsExists) this.syncEntities.add(syncingData);
    if (!this.isSyncStarted && isRequestsExists) {
      this.props.setProgressSync({ progress: 10, syncingData });
      this.isSyncStarted = true;
    }
  };

  // Unsent feed orders win over a success message: the user has to act on them
  getSyncedData = () => {
    const entities = this.syncEntities;
    if (entities.has('feedOrdersNotSynced')) return 'feedOrdersNotSynced';
    if (entities.size === 1) return [...entities][0];
    if (entities.size === 2 && entities.has('syncingDC') && entities.has('syncingFeedOrders')) {
      return 'syncingDCAndFeedOrders';
    }
    return 'syncingDC';
  };

  finishSync = () => {
    if (this.isSyncStarted) {
      const syncingData = this.getSyncedData();
      this.props.setProgressSync({ progress: 100, syncingData });
      this.isSyncStarted = false;
      this.syncEntities.clear();
    }
  };

  makeReFetchData = (requestsCount) => {
    const { pathname, fetchFarmGroups, fetchFarms, params } = this.props;
    if (requestsCount && isCheckupPigGroupsLocation(pathname)) {
      fetchFarmGroups(params.id);
    }
    if (requestsCount && isCheckupFarmsLocation(pathname)) {
      fetchFarms(params.id);
    }
  };

  initialPreloadData = () => {
    const { current_company_id, setNetworkConnect } = this.props;
    if (navigator.onLine) {
      this.preloadDataForIndexedDB(current_company_id)
        .then(() => {
          if (current_company_id) this.handleOnline();
        })
        // IndexedDB is not open yet (e.g. blocked by another tab); offline data is loaded on the next app load
        .catch(() => null);
    } else {
      setNetworkConnect({ isOnline: false });
    }
  };

  handleOnline = () => {
    const { setNetworkConnect } = this.props;
    setNetworkConnect({ isOnline: true });

    execDailyCheckupsSync()
      .then((requests) => {
        this.startSync(requests.length, 'syncingDC');
        return Promise.all(requests)
          .then(() => execMediaSync())
          .then((mediaRequests) => {
            this.startSync(mediaRequests.length, 'syncingDC');
            return Promise.all(mediaRequests);
          })
          .then(() => {
            this.finishSync();
            this.makeReFetchData(requests.length);
          });
      })
      // Feed orders do not depend on checkups, so a failed checkup sync does not hold them back.
      // The failed checkups stay queued and are not reported as synced.
      .catch(() => this.syncEntities.delete('syncingDC'))
      .then(() => this.syncFeedOrders());
  };

  syncFeedOrders = (isRetry = false) => {
    const { current_user_id, current_company_id } = this.props;
    if (!current_user_id || !current_company_id) return Promise.resolve();

    return execFeedOrdersSync({
      userId: current_user_id,
      companyId: current_company_id,
      // A timer retry stays quiet until an order is sent or rejected, or the banner shows every minute
      onStart: isRetry ? null : (ordersCount) => this.startSync(ordersCount, 'syncingFeedOrders'),
    }).then(({ queuedCount, sentCount, failedCount }) => {
      if (isRetry && !sentCount && !failedCount) return;
      // Every queued order was removed by the user meanwhile: close the banner the start opened
      if (!queuedCount) {
        this.finishSync();
        return;
      }
      if (isRetry) this.startSync(queuedCount, 'syncingFeedOrders');
      // Failed or still queued orders are listed on the Feed Orders pages, so the banner points there
      if (sentCount < queuedCount) {
        this.syncEntities.delete('syncingFeedOrders');
        this.syncEntities.add('feedOrdersNotSynced');
      }
      this.finishSync();
    });
  };

  // An order queued on a weak signal gets no 'online' event while the browser still counts as online
  retryFeedOrdersSync = () => {
    if (this.props.isFeedOrdersEnabled && navigator.onLine && !this.isSyncStarted) this.syncFeedOrders(true);
  };

  handleOffline = () => {
    this.props.setNetworkConnect({ isOnline: false });
  };

  sendRequestsForOffline = (companyId) => {
    const { setProgressSync, setForceSync } = this.props;
    setProgressSync({ progress: 10, syncingData: 'preloadData' });
    const requests = [
      getFarmsForOffline(),
      getPigGroupsForOffline(),
      getTreatments(),
      getSymptoms(),
      getMortalityReasons(),
      getTemperaturePoints(),
      updateLastPreloadData(companyId),
      clearOfflineCheckups(),
      clearOfflineRequests(),
      clearOfflineMediaRequests(),
      clearFeedOrdersListsOffline(),
    ];
    return Promise.all(requests)
      .then(() => this.preloadFeedOrders(companyId, true))
      .then(() => {
        setProgressSync({ progress: 100, syncingData: 'preloadData' });
        setForceSync(false);
      });
  };

  // Feed order data has its own date, so a device that preloaded before it existed gets it the same day
  // without the full preload, which drops today's unsynced checkups. Runs after updateLastPreloadData:
  // both merge into one IndexedDB record.
  preloadFeedOrders = (companyId, isForced = false) => {
    if (!this.props.isFeedOrdersEnabled) return Promise.resolve();

    return getLastPreloadData(companyId)
      .then((preloadData = {}) => {
        const loadedAt = preloadData.feed_orders_loaded_at;
        if (!isForced && loadedAt && moment().isSame(loadedAt, 'day')) return null;
        // A failed refresh is retried on the next app load, not tomorrow
        return preloadFeedOrdersData().then((isLoaded) => isLoaded && updateFeedOrdersPreloadDate(companyId));
      })
      .catch(() => null);
  };

  preloadDataForIndexedDB = (company_id) => {
    return new Promise((resolve, reject) => {

      if (!company_id) {
        resolve();
        return;
      }

      getLastPreloadData(company_id)
        .then((preloadData = {}) => {

          const isNewDay = !moment().startOf('day').isSame(moment(preloadData.loaded_at).startOf('day'));
          const isForceRefresh = getLSRefreshIndexedDB();

          if (isEmpty(preloadData) || isNewDay || isForceRefresh) {
            removeLSRefreshIndexedDB();
            this.sendRequestsForOffline(company_id).then(resolve).catch(reject);
          } else {
            this.preloadFeedOrders(company_id).then(resolve);
          }
        })
        .catch(reject);
    });
  };

  render() {
    return null;
  }
}

NetworkManager.contextTypes = {
  router: T.object.isRequired,
};

NetworkManager.propTypes = {
  setNetworkConnect: T.func.isRequired,
  fetchFarmGroups: T.func.isRequired,
  fetchFarms: T.func.isRequired,
  current_company_id: T.number,
  current_user_id: T.number,
  isFeedOrdersEnabled: T.bool,
  setProgressSync: T.func.isRequired,
  pathname: T.string.isRequired,
  params: T.object.isRequired,
  setForceSync: T.func.isRequired,
  isForceSync: T.bool.isRequired,
};

export default connect(
  (state) => ({
    current_company_id: state.auth.user?.current_company?.id,
    current_user_id: state.auth.user?.id,
    isFeedOrdersEnabled: !!state.auth.user?.current_company?.feed_orders_tracking,
    isForceSync: state.network.isForceSync,
  }), {
    setNetworkConnect,
    fetchFarmGroups,
    fetchFarms,
    setProgressSync,
    setForceSync,
  }
)(NetworkManager);
